import { useCallback, useEffect, useRef } from 'react';

/**
 * R18 client-audio fix — decoupled audio-level animation.
 *
 * Problem (garble-over-time): the Pipecat client fires onLocalAudioLevel / onRemoteAudioLevel
 * at the audio-worklet frame rate (~100–200 Hz). Each call previously wrote to the Zustand
 * store, causing a React re-render of every level-subscriber per frame. As the transcript
 * grew, those re-renders got heavier and starved the main thread that feeds PCM chunks to the
 * playback AudioWorklet → garbled/choppy audio that worsened over time.
 *
 * Fix: store the latest user/bot level in a shared ref; the VoiceOrb reads from the ref inside
 * its own requestAnimationFrame loop (~30fps) and applies levels imperatively (CSS vars, no
 * React re-render). The Zustand setLevels call is REMOVED from the hot audio-callback path.
 *
 * The store still owns userLevel / botLevel for non-animation consumers (if any), but those
 * are updated via a low-frequency throttled mirror (~15fps) rather than per-frame. In practice,
 * only VoiceOrb reads them; the throttled path covers future consumers safely.
 *
 * Usage:
 *   const { levelRef, onLocalAudioLevel, onRemoteAudioLevel } = useAudioLevels(setLevels);
 *   // Pass onLocal/onRemoteAudioLevel to PipecatClient callbacks.
 *   // Pass levelRef to VoiceOrb so it can animate imperatively.
 */

/** Shared audio-level state — written by the hot callback path, read by the rAF loop. */
export type AudioLevelRef = {
  user: number;
  bot: number;
};

/** Throttle for the low-frequency store mirror (ms). 15fps = ~67ms. */
const STORE_MIRROR_INTERVAL_MS = 67;

export function useAudioLevels(setLevels: (levels: { user?: number; bot?: number }) => void): {
  /** The live level pair — written at the audio callback rate, read by the VoiceOrb rAF loop. */
  levelRef: React.RefObject<AudioLevelRef>;
  /** Drop-in replacement for PipecatClient onLocalAudioLevel — writes ref only (no store write). */
  onLocalAudioLevel: (level: number) => void;
  /** Drop-in replacement for PipecatClient onRemoteAudioLevel — writes ref only (no store write). */
  onRemoteAudioLevel: (level: number) => void;
} {
  const levelRef = useRef<AudioLevelRef>({ user: 0, bot: 0 });

  /**
   * Low-frequency store mirror so any consumer of userLevel/botLevel from the Zustand store
   * still gets reasonably fresh values (~15fps) without a per-frame store write.
   * Cleared on unmount.
   */
  const mirrorTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    mirrorTimerRef.current = setInterval(() => {
      const { user, bot } = levelRef.current;
      setLevels({ user, bot });
    }, STORE_MIRROR_INTERVAL_MS);

    return () => {
      if (mirrorTimerRef.current !== null) {
        clearInterval(mirrorTimerRef.current);
        mirrorTimerRef.current = null;
      }
      // Zero-out the store on cleanup so stale levels don't persist after disconnect.
      setLevels({ user: 0, bot: 0 });
    };
  }, [setLevels]);

  /** Called by PipecatClient onLocalAudioLevel — writes ref only, no store write. */
  const onLocalAudioLevel = useCallback((level: number) => {
    levelRef.current.user = level;
  }, []);

  /** Called by PipecatClient onRemoteAudioLevel — writes ref only, no store write. */
  const onRemoteAudioLevel = useCallback((level: number) => {
    levelRef.current.bot = level;
  }, []);

  return { levelRef, onLocalAudioLevel, onRemoteAudioLevel };
}
